import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Menu } from "@tamagui/lucide-icons";
import {
  type CSSProperties,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Separator } from "../../ui/Separator";

const dragHandleStyle: CSSProperties = {
  border: 0,
  background: 0,
  padding: 0,
  margin: 0,
  position: "absolute",
  left: -24,
  top: 0,
  bottom: 0,
  paddingTop: 17,
  paddingLeft: 24,
  cursor: "grab",
  touchAction: "none",
  width: 24 + 24,
  display: "flex",
  alignItems: "flex-start",
};

function SortableItemWrapper(
  props: PropsWithChildren<{ isDragging?: boolean }>,
) {
  return (
    <div
      style={{
        position: "relative" as "static", // Fattar inte att relative finns här
        paddingLeft: 12 + 12,
        paddingTop: 12,
        paddingBottom: 12,
        opacity: props.isDragging ? 0.2 : undefined,
        background: "rgba(255, 255, 255, 0.75)",
      }}
    >
      {props.children}
    </div>
  );
}

export function SortableItem(
  props: PropsWithChildren<{ id: UniqueIdentifier; index: number }>,
) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
    isDragging,
    newIndex,
  } = useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {newIndex !== 0 ? <Separator noMargins /> : undefined}
      <SortableItemWrapper isDragging={isDragging}>
        <button
          style={dragHandleStyle}
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
        >
          <Menu size={"$small"} color={"$muted"} />
        </button>

        {props.children}
      </SortableItemWrapper>
    </div>
  );
}

export type SortableItem = {
  uid: string;
};

export type SortableItemWithId<T> = {
  id: string;
  item: T;
};

export function SortableList<T extends SortableItem>(props: {
  renderItem: (listItem: SortableItemWithId<T>) => JSX.Element;
  onChange: (newList: SortableItemWithId<T>[]) => void;
  items: T[];
}) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [_items, setItems] = useState<SortableItemWithId<T>[]>(() =>
    props.items.map((item) => ({ item, id: item.uid })),
  );

  useEffect(() => {
    setItems(props.items.map((item) => ({ item, id: item.uid })));
  }, [props.items]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newList = arrayMove(items, oldIndex, newIndex);

        props.onChange(newList);

        return newList;
      });
    }

    setActiveId(null);
  }

  const activeItem = _items.find((listItem) => listItem.id === activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext items={_items} strategy={verticalListSortingStrategy}>
        {_items.map((listItem, index) => (
          <SortableItem key={listItem.id} id={listItem.id} index={index}>
            {props.renderItem(listItem)}
          </SortableItem>
        ))}
      </SortableContext>
      <DragOverlay>
        {activeItem ? (
          <SortableItemWrapper>
            <button type="button" style={dragHandleStyle}>
              <Menu size={"$small"} color={"$muted"} />
            </button>
            {props.renderItem(activeItem)}
          </SortableItemWrapper>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export type SortableDataItem = SortableItem & {
  uid: string;
  render: () => JSX.Element;
  updateIndex: (index: number) => void;
};

export function SortableDataItemList<T extends SortableDataItem>(props: {
  items: T[];
}) {
  const renderItem = useCallback((listItem: SortableItemWithId<T>) => {
    return listItem.item.render();
  }, []);

  const handleChange = useCallback((items: SortableItemWithId<T>[]) => {
    items.forEach((listItem, index) => {
      listItem.item.updateIndex(index);
    });
  }, []);

  return (
    <SortableList
      items={props.items}
      renderItem={renderItem}
      onChange={handleChange}
    />
  );
}
